import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import {
  copy,
  IFilterService,
  IHttpService as $IHttpService,
  ILogService,
  IParseService,
  IPromise,
  IQService,
  isArray,
  isDate,
  isNumber,
  isString,
} from 'angular';
import { i18n } from 'i18next';

import { Configuration } from '@contact/upgrade';

import { ICSRActivty } from './models/csr-activity';
import { UserTrackingFunction } from './models/tracking-function';

/**
 * used for bad linked functions between traces that are created in bl and enum AGENT value
 */
const fonctionFilterKey: { [key: string]: string } = {
  AGENT: 'CSR',
};

export interface IUserTrackingService {
  setFunction(trackingFunction: UserTrackingFunction): void;
  getFunction(): UserTrackingFunction;
  getUpdateData(updateDataId): IPromise<any>;
  getUpdatedHistoryRetention(): IPromise<number>;
  getAdministratorUserList(): IPromise<Array<any>>;
  getUserList(): IPromise<Array<any>>;
  getAgentList(): IPromise<Array<any>>;
  findUserFromUuid(userId): any;
  findSkillFromUuid(skillId): any;
  findCsrFromUuid(csrId): any;
  getSkillList(): IPromise<Array<any>>;
  getCampaignList(): IPromise<Array<any>>;
  getActivityI18nText(i18nSection, type, action, target, params): string;
  getDescriptionText(i18nSection, action, target, label): string;
  getActionText(i18nSection, action, target, params): string;
  getCsrActivityText(action, target?, params?): string;
  getCsrActivities(viewAgents): Array<ICSRActivty>;
  resetFilters(): void;
  updateBeginDate(newDate: Date);
  updateEndDate(newDate: Date);
  getRowEntityText(entity): string;
  queryTrackingElements(): IPromise<Array<any>>;
  getUpdateData(updateDataId): IPromise<any>;
  updateFilters(field: string, value: Array<string> | null): void;
  updateFiltersString(field: string, value: string | null): void;
}

export class UserTrackingService implements IUserTrackingService {
  static $inject: Array<string> = ['$log', '$q', '$parse', '$filter', '$http', 'configuration', 'httpDeprecated', '$i18next'];

  private visibleDomains: Array<any> = [];

  private elements: Array<any> = [];

  private lists: any = {
    administratorUserList: {
      initialized: false,
      items: [],
    },
    userList: {
      initialized: false,
      items: [],
    },
    skillList: {
      initialized: false,
      items: [],
    },
    agentList: {
      initialized: false,
      items: [],
    },
    campaignList: {
      initialized: false,
      items: [],
    },
  };

  private limit: number = 1000;

  private baseQuery: any = {
    from: 0,
    size: this.limit,
    sort: [
      {
        '@timestamp': { order: 'desc' },
      },
    ],
    query: {
      filtered: {
        query: { match_all: {} },
        filter: {
          and: [
            {
              term: {
                'logs.layer.raw': 'cng',
              },
            },
            {
              regexp: {
                'logs.msg.raw': '.*USER_TRACKING.*',
              },
            },
          ],
        },
      },
    },
  };

  private agentView = null;

  private beginDate: any = null;

  private endDate: any = null;

  private filters = {};

  private unwrapTrackingResult = this.$parse('data.hits.hits');

  private csrActivities: Array<ICSRActivty> = [];

  private updatedHistoryRetention: any = null;

  // iso-8601
  private dateFormat = 'yyyy-MM-ddTHH:mm:ss.sss';

  // for rest
  private indexDateFormat = 'yyyyMMdd';

  constructor(
    private readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly $parse: IParseService,
    private readonly $filter: IFilterService,
    private readonly $http: $IHttpService,
    private readonly configuration: Configuration,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly $i18next: i18n
  ) {}

  public getUpdatedHistoryRetention(): IPromise<number> {
    let deferred = this.$q.defer<number>();

    if (this.updatedHistoryRetention === null) {
      this.httpDeprecated
        .asPromise()
        .get('protected/user_tracking/getUpdatedHistoryRetention')
        .then(
          success => {
            if (isNumber(success.data)) {
              this.updatedHistoryRetention = success.data;
            } else {
              this.updatedHistoryRetention = 0;
            }
            deferred.resolve(this.updatedHistoryRetention);
          },
          () => deferred.reject()
        );
    } else {
      deferred.resolve(this.updatedHistoryRetention);
    }

    return deferred.promise;
  }

  private getUsers(listToUse: string, partialGetUrl: string): IPromise<Array<any>> {
    let deferred = this.$q.defer<Array<any>>();

    if (!this.lists[listToUse].initialized) {
      this.lists[listToUse].initialized = true;
      this.$log.debug('[tracking service]list [' + listToUse + '] was not initialized');
      this.httpDeprecated
        .asPromise()
        .get('protected/user_tracking/' + partialGetUrl)
        .then(
          success => {
            if (isArray(success.data)) {
              this.lists[listToUse].items = this.$filter('orderBy')(success.data, ['firstName', 'lastName'], false);
            } else {
              this.lists[listToUse].items = [];
            }
            this.$log.debug('[tracking service]receive[' + this.lists[listToUse].items.length + '] for list [' + listToUse + ']');
            deferred.resolve(this.lists[listToUse].items);
          },
          () => {
            this.$log.info('[tracking service]error when retrieving elements for list [' + listToUse + ']');
            deferred.reject();
          }
        );
    } else {
      this.$log.debug(
        '[tracking service]list [' + listToUse + '] was initialized and contains[' + this.lists[listToUse].items.length + '] elements'
      );
      deferred.resolve(this.lists[listToUse].items);
    }

    return deferred.promise;
  }

  public getAdministratorUserList(): IPromise<Array<any>> {
    return this.getUsers('administratorUserList', 'getAdministrators');
  }

  public getUserList(): IPromise<Array<any>> {
    return this.getUsers('userList', 'getUsers');
  }

  public getAgentList(): IPromise<Array<any>> {
    return this.getUsers('agentList', 'getCsrs');
  }

  public findUserFromUuid(userId): any {
    let result = this.$filter('filter')(this.lists.userList.items, { uuid: userId });
    if (result.length === 1) {
      return result[0];
    }
    return null;
  }

  public findSkillFromUuid(skillId): any {
    let result = this.$filter('filter')(this.lists.skillList.items, { uuid: skillId });
    if (result.length === 1) {
      return result[0];
    }
    return null;
  }

  public findCsrFromUuid(csrId): any {
    let result = this.$filter('filter')(this.lists.agentList.items, { uuid: csrId });
    if (result.length === 1) {
      return result[0];
    }
    return null;
  }

  public getSkillList(): IPromise<Array<any>> {
    let deferred = this.$q.defer<Array<any>>();

    if (!this.lists.skillList.initialized) {
      this.lists.skillList.initialized = true;
      this.httpDeprecated
        .asPromise()
        .get('protected/user_tracking/getSkills')
        .then(
          success => {
            if (isArray(success.data)) {
              this.lists.skillList.items = this.$filter('orderBy')(success.data, 'name', false);
            } else {
              this.lists.skillList.items = [];
            }
            deferred.resolve(this.lists.skillList.items);
          },
          () => deferred.reject()
        );
    } else {
      deferred.resolve(this.lists.skillList.items);
    }

    return deferred.promise;
  }

  public getCampaignList(): IPromise<Array<any>> {
    let deferred = this.$q.defer<Array<any>>();
    if (!this.lists.campaignList.initialized) {
      this.lists.campaignList.initialized = true;
      this.$http.get<any>(this.configuration.campaignBaseUrl + '/protected/campaign').then(
        success => {
          if (success.data.hasOwnProperty('content') && isArray(success.data['content'])) {
            this.lists.campaignList.items = this.$filter('orderBy')(success.data['content'], 'name', false);
          } else {
            this.lists.campaignList.items = [];
          }
          deferred.resolve(this.lists.campaignList.items);
        },
        () => deferred.reject()
      );
    } else {
      deferred.resolve(this.lists.campaignList.items);
    }

    return deferred.promise;
  }

  // COMMON
  public getActivityI18nText(i18nSection, type, action, target, params): string {
    if (target) {
      if (params) {
        return this.$i18next.t('tracking:' + i18nSection + '.activity.' + action + '_' + target + '.' + type, params);
      } else {
        return this.$i18next.t('tracking:' + i18nSection + '.activity.' + action + '_' + target + '.' + type);
      }
    } else {
      if (params) {
        return this.$i18next.t('tracking:' + i18nSection + '.activity.' + action + '.' + type, params);
      } else {
        return this.$i18next.t('tracking:' + i18nSection + '.activity.' + action + '.' + type);
      }
    }
  }

  public getDescriptionText(i18nSection, action, target, label): string {
    return this.getActivityI18nText(i18nSection, 'description', action, target, { label: label });
  }

  public getActionText(i18nSection, action, target, params): string {
    return this.getActivityI18nText(i18nSection, 'action', action, target, params);
  }

  // CSR
  public getCsrActivityText(action, target?, params?): string {
    return this.getActionText(UserTrackingFunction[UserTrackingFunction.AGENT], action, target, params);
  }

  private addCsrActivity(action, target?) {
    if (target) {
      this.csrActivities.push({ action: action, target: target, label: this.getCsrActivityText(action, target) });
    } else {
      this.csrActivities.push({ action: action, label: this.getCsrActivityText(action) });
    }
  }

  public buildActivities(): void {
    this.addCsrActivity('CONNECT');
    this.addCsrActivity('DISCONNECT');

    this.addCsrActivity('RESERVE', 'POSITION');
    this.addCsrActivity('RESERVE', 'PRIVATE_POSITION');
    this.addCsrActivity('AVAILABLE', 'POSITION');

    this.addCsrActivity('SUBMIT', 'POST_TREATMENT');
    this.addCsrActivity('END', 'POST_TREATMENT');
    this.addCsrActivity('EXTEND', 'POST_TREATMENT');

    this.addCsrActivity('ACCEPT', 'SERVICE_RESPONSE');
    this.addCsrActivity('REFUSE', 'SERVICE_RESPONSE');

    this.addCsrActivity('ASK', 'PAUSE');
    this.addCsrActivity('CANCEL', 'PAUSE');
    this.addCsrActivity('CANCEL', 'VALIDATED_PAUSE_REQUEST');
    this.addCsrActivity('FORCE', 'PAUSE');
    this.addCsrActivity('END', 'PAUSE');

    this.addCsrActivity('ASK', 'TASK');
    this.addCsrActivity('CANCEL', 'TASK');
    this.addCsrActivity('CANCEL', 'VALIDATED_TASK_REQUEST');
    this.addCsrActivity('FORCE', 'TASK');
    this.addCsrActivity('END', 'TASK');

    this.addCsrActivity('PARK', 'PARTICIPANT');
    this.addCsrActivity('UNPARK', 'PARTICIPANT');
    this.addCsrActivity('CANCEL', 'CONFERENCE');
    this.addCsrActivity('CREATE', 'CONFERENCE');
    this.addCsrActivity('CANCEL', 'TRANSFER');

    this.addCsrActivity('TRANSFER', 'CONTACT');
    this.addCsrActivity('TRANSFER_TO_TEAM', 'CONTACT');
    this.addCsrActivity('REDIRECT', 'CONTACT');
    this.addCsrActivity('PARK', 'CONTACT');
    this.addCsrActivity('UNPARK', 'CONTACT');
    this.addCsrActivity('DISCONNECT', 'CONTACT');

    this.addCsrActivity('ASK', 'OUTGOING_CALL_TASK');
    this.addCsrActivity('CREATE', 'OUTGOING_CALL');
    this.addCsrActivity('END', 'OUTGOING_CALL');

    this.addCsrActivity('START', 'RECORD');
    this.addCsrActivity('REMOVE', 'RECORD');
    this.addCsrActivity('INTERRUPT', 'RECORD');
    this.addCsrActivity('RESUME', 'RECORD');
    this.addCsrActivity('END', 'RECORD');
    this.addCsrActivity('RESTRICT', 'RECORD');
    this.addCsrActivity('UNRESTRICT', 'RECORD');

    this.addCsrActivity('ADD', 'SKILL');
    this.addCsrActivity('REMOVE', 'SKILL');

    this.addCsrActivity('UPDATE', 'CLIENT_FILE');

    this.addCsrActivity('SEND', 'SMS');

    this.addCsrActivity('REDIRECT', 'SCRIPT');

    this.addCsrActivity('OPEN', 'MAIL_BASKET');
    this.addCsrActivity('CLOSE', 'MAIL_BASKET');

    this.addCsrActivity('SEND', 'MAIL');

    this.addCsrActivity('OPEN', 'EXTERNAL_SERVICE');
    this.addCsrActivity('CLOSE', 'EXTERNAL_SERVICE');

    this.addCsrActivity('CONSENT', 'CLIENT_FILE');
    this.addCsrActivity('CONSENT_IMPLICIT', 'CLIENT_FILE');

    this.addCsrActivity('CLOSE_AND_QUALIFY', 'APPOINTMENT');
    this.addCsrActivity('CANCEL', 'APPOINTMENT');
  }

  public setAgentView(value) {
    this.agentView = value;
  }

  public getCsrActivities(viewAgents): Array<ICSRActivty> {
    if (this.agentView === null && viewAgents !== null) {
      this.setAgentView(viewAgents);
    }
    if (this.agentView !== null && this.csrActivities.length === 0) {
      this.buildActivities();
      this.csrActivities = this.$filter('orderBy')(this.csrActivities, 'label', false);
    }

    return this.csrActivities;
  }

  // QUERY
  public resetFilters(): void {
    let filter;
    for (filter in this.filters) {
      if (this.filters.hasOwnProperty(filter) && filter !== 'function') {
        delete this.filters[filter];
      }
    }
  }

  public setFunction(trackingFunction: UserTrackingFunction): void {
    this.resetFilters();
    this.filters['function'] = UserTrackingFunction[trackingFunction];
  }

  public getFunction(): UserTrackingFunction {
    return this.filters['function'];
  }

  public updateFilters(field: string, value: Array<string> | null): void {
    if (!isString(field)) {
      return;
    }

    if (value !== null && (isString(value) || isArray(value))) {
      this.filters[field] = value;
    } else if (this.filters.hasOwnProperty(field)) {
      delete this.filters[field];
    }
  }

  public updateFiltersString(field: string, value: string | null): void {
    if (!isString(field)) {
      return;
    }

    if (value !== null && isString(value)) {
      this.filters[field] = value;
    } else if (this.filters.hasOwnProperty(field)) {
      delete this.filters[field];
    }
  }

  private getQueryPartForFilter(field, value) {
    let result: any = {};
    if (isArray(value)) {
      result.terms = {};
      result.terms['logs.' + field + '.raw'] = value;
    } else if (isString(value)) {
      if (value !== 'NOT_EXISTS') {
        result.term = {};
        result.term['logs.' + field + '.raw'] = value;
      } else {
        return this.getQueryPartForNonExistingFilter('targetType');
      }
    }
    return result;
  }

  private getQueryPartForNonExistingFilter(field) {
    let result: any = {};

    result.not = {};
    result.not.exists = { field: field };

    return result;
  }

  public updateBeginDate(newDate: Date) {
    this.beginDate = newDate;
  }

  public updateEndDate(newDate: Date) {
    this.endDate = newDate;
  }

  public addDateQuery(query) {
    // begin / end date
    let dateQuery: any = {};

    // begin date
    if (this.beginDate !== null && isDate(this.beginDate)) {
      dateQuery.from = this.$filter('date')(this.beginDate, this.dateFormat, 'UTC') + 'Z';
    }

    // end date
    if (this.endDate !== null && isDate(this.endDate)) {
      dateQuery.to = this.$filter('date')(this.endDate, this.dateFormat, 'UTC') + 'Z';
    }

    query.query.filtered.filter.and.push({ range: { '@timestamp': dateQuery } });
  }

  public buildQuery(): any {
    if (!this.filters.hasOwnProperty('function')) {
      return null;
    }

    let query: any = copy(this.baseQuery);

    this.addDateQuery(query);

    // add domains filters
    query.query.filtered.filter.and.push(this.getQueryPartForFilter('domainId', this.visibleDomains));

    // add ihm filters
    for (let filter in this.filters) {
      if (this.filters.hasOwnProperty(filter)) {
        let filterValue = this.filters[filter];
        if (fonctionFilterKey.hasOwnProperty(filterValue)) {
          filterValue = fonctionFilterKey[filterValue];
        }
        query.query.filtered.filter.and.push(this.getQueryPartForFilter(filter, filterValue));
      }
    }

    return query;
  }

  public getRowEntityText(entity): string {
    return 'commons:basic.entity.' + entity.type + ' <i>' + entity.label + '</i>';
  }

  private buildAndSendQuery(): IPromise<Array<any>> {
    let deferred = this.$q.defer<Array<any>>();

    let query = this.buildQuery();
    if (query === null) {
      this.elements = [];
      return deferred.promise;
    }

    let params: any = [];
    if (this.beginDate !== null && isDate(this.beginDate)) {
      params.push(this.$filter('date')(this.beginDate, this.indexDateFormat));
      if (this.endDate !== null && isDate(this.endDate)) {
        params.push(this.$filter('date')(this.endDate, this.indexDateFormat));
      } else {
        params.push(this.$filter('date')(this.beginDate, this.indexDateFormat));
      }
    } else {
      let currentDate = new Date();
      params.push(this.$filter('date')(currentDate, this.indexDateFormat));
      params.push(this.$filter('date')(currentDate, this.indexDateFormat));
    }

    this.httpDeprecated
      .asPromise()
      .withData(query)
      .post('protected/user_tracking/executeRequest', params)
      .then(
        data => {
          this.elements = this.unwrapTrackingResult(data);
          if (this.elements === undefined || !isArray(this.elements)) {
            this.elements = [];
          }
          deferred.resolve(this.elements);
        },
        () => {
          deferred.reject();
        }
      );

    return deferred.promise;
  }

  public queryTrackingElements(): IPromise<Array<any>> {
    let deferred = this.$q.defer<Array<any>>();

    // add domains
    if (this.visibleDomains.length === 0) {
      this.httpDeprecated
        .asPromise()
        .get('protected/user_tracking/getVisibleDomains')
        .then(
          success => {
            if (isArray(success.data)) {
              this.visibleDomains = success.data;
              this.buildAndSendQuery().then(elements => {
                deferred.resolve(elements);
              });
            }
          },
          () => {
            deferred.reject();
          }
        );
    } else {
      this.buildAndSendQuery().then(elements => {
        deferred.resolve(elements);
      });
    }

    return deferred.promise;
  }

  public getUpdateData(updateDataId): IPromise<any> {
    return this.httpDeprecated.asPromise().get('protected/user_tracking/getUpdateData', [updateDataId]);
  }
}

export default 'tracking';
